import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Datepicker, Popup } from 'next-common-lib';
import { useFormik } from 'formik';
import i18n from 'i18n';
import TextFieldFormik from 'components/Common/InputBox';
import TblList from 'components/Common/Template/TblList';
import PopConfirmButton from 'components/Common/Buttons/PopConfirmButton';
import TblCell from 'components/Common/Template/TblCell';
import * as wizardActions from 'redux/ducks/common/wizardDuck';
import * as pimsActions from 'redux/ducks/Anniversary/AnniversaryDucks';
import AnniversarySchema from './AnniversarySchema';

export const PopupAnniversary = () => {
  const dispatch = useDispatch();
  const isPopup = useSelector(state => state.wizard.isPopup);
  const anniversary = useSelector(state => state.anniversary);

  const formik = useFormik({
    initialValues: {
      subject: '',
      restDayYn: 'N',
      date: '',
      lunarYn: 'N',
      recurrence: 'N',
      freqInterval: '1',
      endType: 'UNTIL',
      freq: 'YEARLY',
      condition: 'D',
      until: 'UNLIMITED',
    },
    validationSchema: AnniversarySchema,
    onSubmit: values => {
      const payload = {
        data: {
          allDayYn: 'Y',
          startTime: { date: values.date },
          subject: values.subject,
          endTime: { date: values.date },
          restDayYn: values.restDayYn, // 휴일,
        },
        code: anniversary.subscribedId,
      };

      if (values.recurrence === 'Y') {
        payload.data.recurrence = {
          lunarYn: values.lunarYn,
          freqInterval: values.freqInterval,
          endType: values.endType,
          freq: values.freq,
          condition: values.condition,
          until: values.until,
        };
      }
      dispatch(pimsActions.createHoliday(payload));
    },
  });

  const handleOnClose = () => {
    formik.handleReset();
    dispatch(wizardActions.closePopup());
  };

  useEffect(() => {
    if (anniversary.isSuccess) {
      handleOnClose();
    }
  }, [anniversary.isSuccess]);

  const onSelectedChanged = named => {
    formik.setFieldValue(named, formik.getFieldProps(named).value === 'Y' ? 'N' : 'Y');
  };

  const checkErrorStatusDate = () => {
    if (formik.submitCount > 0 && !!formik.getFieldMeta('date').error) return true;
    return false;
  };
  const isDate = () => {
    if (formik.getFieldProps('date').value) return true;
    return false;
  };

  return (
    <>
      {isPopup && (
        <Popup
          id="departmentPopup"
          isShow={isPopup}
          isDimed={isPopup}
          dimedClassName="transparent"
          zIndex={2001}
          isDimedOverlap
          size="sm w-popup"
          useScroll={false}
          componentType="normalPopup"
          closeFunction={() => {
            handleOnClose();
          }}
          windowScrollInfo={{
            scrollView: {
              x: false,
              y: true,
            },
          }}
          headerComponent={[
            <h1 className="popup-title" key={1}>
              {i18n.t('BTY_W_T_ADD_COMP_ANNIVERSARY')}
            </h1>,
          ]}
          contentsComponent={[
            <div className="article-body anniversary">
              <TblList>
                <TblCell isHeader isRequired>
                  {i18n.t('BTY_W_T_ANIV_NAME')}
                </TblCell>
                <TblCell>
                  <TextFieldFormik
                    id="subject"
                    formik={formik}
                    placeholderLabel={i18n.t('BTY_W_T_ANIV_NAME_GUIDE')}
                    maxLength={30}
                    useExpLabel={formik.errors.subject !== undefined}
                    autoFocus
                  />
                </TblCell>
              </TblList>
              <TblList>
                <TblCell isHeader isRequired>
                  {i18n.t('BTY_W_T_ANIV_DATE')}
                </TblCell>
                <TblCell>
                  <div className={`input-group-wrap ${checkErrorStatusDate() ? 'error' : ''}`}>
                    <Datepicker
                      isDatePickerInput
                      className="w-full"
                      inputPlaceholder={i18n.t('BTY_W_T_ANIV_DATE_GUIDE')}
                      autoSetValue
                      componentType="button"
                      layerComponent={
                        <button className="datepicker-selector input-group" type="button">
                          {isDate() ? (
                            <span className="text">{formik.getFieldProps('date').value}</span>
                          ) : (
                            <span className="text hint">{i18n.t('BTY_W_T_ANIV_DATE_GUIDE')}</span>
                          )}
                        </button>
                      }
                      onClick={e => {
                        formik.setFieldValue('date', e);
                      }}
                      label={formik.getFieldProps('date').value}
                    />
                    {checkErrorStatusDate() && (
                      <div className="input-add-exp error">
                        <span className="text color-warning">{i18n.t(formik.getFieldMeta('date').error)}</span>
                      </div>
                    )}
                  </div>
                </TblCell>
              </TblList>
              <TblList>
                <TblCell isHeader>{i18n.t('BTY_W_T_ANIV_SELECT')}</TblCell>
                <TblCell>
                  <Checkbox
                    id="restDayYn"
                    label={i18n.t('BTY_W_T_HOLYIDY')}
                    onClick={() => {
                      onSelectedChanged('restDayYn');
                    }}
                    tooltipInfo={{
                      title: `${i18n.t('BTY_W_T_HOLYIDY')}`,
                    }}
                  />
                  <Checkbox
                    id="recurrence"
                    label={i18n.t('BTY_W_T_RECURRENCE')}
                    onClick={() => {
                      onSelectedChanged('recurrence');
                    }}
                    tooltipInfo={{
                      title: `${i18n.t('BTY_W_T_RECURRENCE')}`,
                    }}
                  />
                  {formik.getFieldProps('recurrence').value === 'Y' && (
                    <Checkbox
                      id="lunarYn"
                      label={i18n.t('BTY_W_T_LUNAR')}
                      onClick={() => {
                        onSelectedChanged('lunarYn');
                      }}
                      tooltipInfo={{
                        title: `${i18n.t('BTY_W_T_LUNAR')}`,
                      }}
                    />
                  )}
                </TblCell>
              </TblList>
              <TblList>
                <TblCell isHeader />
                <TblCell>
                  <p className="sub-text">{i18n.t('BTY_W_T_LUNAR_GUIDE')}</p>
                </TblCell>
              </TblList>
            </div>,
          ]}
          bottomComponent={[<PopConfirmButton confirmAction={formik.submitForm} cancelAction={handleOnClose} />]}
        />
      )}
    </>
  );
};
PopupAnniversary.propTypes = {};
PopupAnniversary.defaultProps = {};
export default PopupAnniversary;
